import { userFields } from '@openx/components-administration/lib/constants';
import { useAdministrationContext } from '@openx/components-administration/lib/utils/context/AdministrationContext';
import type { OptionWithUid } from '@openx/components-administration/lib/utils/context/UserHookTypes';
import type { UserFormData } from '@openx/components-administration/types';
import { RHFAutoComplete } from '@openx/components-core/lib/AutoComplete/RHFAutoComplete';
import { RHFAutoCompleteVirtualize } from '@openx/components-core/lib/AutoComplete/RHFAutoCompleteVirtualize';
import { FormSection } from '@openx/components-core/lib/FormSection/FormSection';
import { RHFTextField } from '@openx/components-core/lib/TextField/RHFTextField';
import { RIGHT_COLUMN_WIDTH } from '@openx/constants/form';
import { OPEN_AUDIENCE_ORG_NAME } from '@openx/utils/services/config';
import { useCallback, useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type UserDetailsSectionProps = {
  isEdit?: boolean;
};

export const UserDetailsSection = ({ isEdit }: UserDetailsSectionProps) => {
  const { t } = useTranslation();
  const { useAccountOptionsFetch } = useAdministrationContext();
  const { accounts } = useAccountOptionsFetch();
  const observableAccountUid = useWatch<Pick<UserFormData, 'account'>>({ name: userFields.ACCOUNT });
  const { setValue } = useFormContext();

  const parentAccount = useMemo(
    () => accounts.list.find(account => account.name === OPEN_AUDIENCE_ORG_NAME),
    [accounts]
  );

  const handleOnManageAccountChange = useCallback(
    (_: React.SyntheticEvent, value: NonNullable<OptionWithUid>) => {
      setValue(userFields.ACCOUNT, value.id);
      setValue('isRoot', [null, '', parentAccount?.id].includes(value.id));
    },
    [setValue, parentAccount?.id]
  );

  useEffect(() => {
    if (!isEdit && parentAccount) {
      setValue(userFields.ACCOUNT, parentAccount.id);
    }
  }, [parentAccount, isEdit, setValue]);

  return (
    <FormSection title={t('User Details')} rightColumnSize={RIGHT_COLUMN_WIDTH}>
      <RHFTextField
        name={userFields.FIRST_NAME}
        label={t('First Name')}
        placeholder={t('Enter First Name')}
        fullWidth
        dataTest="first-name"
      />

      <RHFTextField
        name={userFields.LAST_NAME}
        label={t('Last Name')}
        placeholder={t('Enter Last Name')}
        fullWidth
        dataTest="last-name"
        sx={{ marginTop: 1 }}
      />

      <RHFTextField
        name={userFields.EMAIL}
        label={t('Email')}
        placeholder={t('Enter Email')}
        fullWidth
        dataTest="email"
        sx={{ marginTop: 1 }}
        disabled={isEdit}
      />

      <RHFAutoCompleteVirtualize
        name={userFields.ACCOUNT}
        textFieldProps={{
          label: t('Organization'),
          margin: 'dense',
          placeholder: t('[ Select Organization ]'),
        }}
        disableClearable
        renderOptions={{
          dataTest: 'organization-item',
          tooltip: true,
        }}
        getOptionLabel={option => option.name ?? ''}
        options={accounts.list}
        value={accounts.list.find(account => account.id === observableAccountUid)}
        loading={!!accounts.loading}
        onChange={handleOnManageAccountChange}
        data-test="organization"
        disabled={isEdit}
      />

      {isEdit && (
        <RHFAutoComplete
          name={userFields.ROLE}
          limitTags={6}
          multiple
          options={[]}
          sx={{ mt: 1 }}
          textFieldProps={{
            label: t('Roles'),
          }}
          data-test="roles"
          disabled
        />
      )}
    </FormSection>
  );
};
